import { useEffect, useState } from "react";

/**
 * Loads the PayPal JS SDK on demand, and tells the caller when it is ready.
 *
 * INJECTED RATHER THAN INSTALLED, for the same two reasons useGoogleMaps is.
 * A dependency means editing front/package.json, which makes deploy.sh take
 * its npm-install branch and re-resolve a 2021 tree (CLAUDE.md section 4);
 * and a script in public/index.html would load PayPal on the blog, the area
 * pages and the 404, none of which sell anything.
 *
 * A READY FLAG rather than just `async`, because the buttons are rendered by
 * window.paypal and a component that calls it before the script lands throws
 * during render - which unmounts the tree and gives a blank page, on the one
 * screen where a blank page costs money.
 *
 * THE CLIENT ID COMES FROM THE SERVER, not from the build. It is public by
 * design - it has to reach the browser for the buttons to work - but putting
 * it in front/.env would bake it into the bundle at build time, so moving
 * from sandbox to live would need a rebuild and a deploy rather than a
 * restart. /payments/catalogue reports whichever id the api is configured
 * with, so the two can never disagree about which environment they are in.
 */

/*
 * Module-level, so several components mounting at once share one script tag
 * rather than racing to append their own. Keyed by the parameters, because a
 * different client id or currency is a genuinely different script and the
 * SDK will not reconfigure itself once loaded.
 */
const loaded = {};

function load(clientId, currency, intent) {
    const key = [clientId, currency, intent].join("|");
    if (loaded[key]) return loaded[key];

    loaded[key] = new Promise((resolve, reject) => {
        if (!clientId) {
            reject(new Error("No PayPal client id"));
            return;
        }

        /*
         * `vault` and `intent=subscription` are what the SDK needs to offer
         * subscription buttons at all. Asking for both capabilities up front
         * means one script for both kinds of purchase, rather than tearing
         * the SDK down and reloading it when somebody switches from a single
         * report to a plan.
         */
        const params = new URLSearchParams({
            "client-id": clientId,
            currency: currency || "GBP",
            intent: intent || "capture",
            components: "buttons",
            "disable-funding": "credit,paylater",
        });
        if (intent === "subscription") params.set("vault", "true");

        const script = document.createElement("script");
        script.src = "https://www.paypal.com/sdk/js?" + params.toString();
        script.async = true;
        script.onload = () => {
            if (window.paypal) resolve(window.paypal);
            else reject(new Error("PayPal loaded but exposed nothing"));
        };
        script.onerror = () => {
            /*
             * Forgotten, so a later attempt can try again. A cached rejected
             * promise would mean one blocked request - an ad blocker, a
             * flaky connection - permanently disabling checkout for the rest
             * of the session.
             */
            delete loaded[key];
            reject(new Error("PayPal could not be loaded"));
        };
        document.body.appendChild(script);
    });

    return loaded[key];
}

/**
 * @param clientId from /payments/catalogue; null until it arrives
 * @param intent "capture" for a one-off, "subscription" for a plan
 * @returns { ready, error } - render buttons only once ready is true
 */
export default function usePayPal(clientId, intent) {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        setReady(false);
        setError(null);

        if (!clientId) return undefined;

        load(clientId, "GBP", intent)
            .then(() => {
                if (alive) setReady(true);
            })
            .catch((e) => {
                if (alive) setError(e.message || "PayPal could not be loaded");
            });

        /*
         * The script itself is deliberately NOT removed on unmount. It is
         * shared, the SDK registers globals that do not unregister cleanly,
         * and somebody who leaves the checkout and comes back should not
         * wait for it twice.
         */
        return () => {
            alive = false;
        };
    }, [clientId, intent]);

    return { ready, error };
}
